import { useQuery } from '@tanstack/react-query';
import { type Db, must, useDb } from './db';

export type MemberKind = 'student' | 'guardian' | 'staff';
export type StaffRole = 'teacher' | 'admin' | 'director' | 'deputy' | 'psychologist';

export interface Membership {
  member_id: string;
  school_id: string;
  school_name: string;
  kind: MemberKind;
  roles: StaffRole[];
}

/** Result of the `public.me()` RPC. `aal` comes from the JWT the database actually checks. */
export interface Me {
  user_id: string;
  aal: 'aal1' | 'aal2';
  mfa_required: boolean;
  memberships: Membership[];
}

export interface ClassRef {
  id: string;
  grade: number;
  label: string;
}

export interface ChildRef {
  memberId: string;
  firstName: string;
  lastName: string;
  class?: ClassRef;
}

interface Base {
  /** stable id of the context, e.g. for remembering the last choice */
  key: string;
  schoolId: string;
  schoolName: string;
  memberId: string;
}

/**
 * One way the person uses the app. A mother who also teaches has three: her two children and
 * her teaching; the UI switches between them.
 */
export type ViewContext =
  | (Base & { kind: 'student'; child: ChildRef })
  | (Base & { kind: 'guardian'; child: ChildRef })
  | (Base & { kind: 'teacher'; homeroomClass?: ClassRef })
  | (Base & { kind: 'admin'; roles: StaffRole[] })
  /** the school psychologist: «Турбота» — threads, the mood of classes, meetings */
  | (Base & { kind: 'psychologist' });

const ADMIN_ROLES: ReadonlyArray<StaffRole> = ['admin', 'director', 'deputy'];

export const classLabel = (c: { grade: number; letter: string }) => `${c.grade}-${c.letter}`;

export async function fetchViewContexts(db: Db, me: Me): Promise<ViewContext[]> {
  const guardianIds = me.memberships.filter((m) => m.kind === 'guardian').map((m) => m.member_id);
  const studentIds = me.memberships.filter((m) => m.kind === 'student').map((m) => m.member_id);
  const staffIds = me.memberships.filter((m) => m.kind === 'staff').map((m) => m.member_id);

  const children =
    guardianIds.length === 0
      ? []
      : must(
          await db
            .from('guardianships')
            .select(
              'guardian_member_id, student:school_members!guardianships_school_id_student_member_id_fkey(id, first_name, last_name)',
            )
            .in('guardian_member_id', guardianIds)
            .eq('status', 'active'),
        );
  const childIds = children.map((c) => c.student.id);
  const allStudentIds = [...studentIds, ...childIds];
  const enrollments =
    allStudentIds.length === 0
      ? []
      : must(
          await db
            .from('class_enrollments')
            .select(
              'student_member_id, class:classes!class_enrollments_school_id_class_id_fkey(id, grade, letter)',
            )
            .in('student_member_id', allStudentIds),
        );
  const homerooms =
    staffIds.length === 0
      ? []
      : must(
          await db
            .from('classes')
            .select('id, grade, letter, homeroom_member_id')
            .in('homeroom_member_id', staffIds),
        );
  const self =
    studentIds.length === 0
      ? []
      : must(
          await db.from('school_members').select('id, first_name, last_name').in('id', studentIds),
        );

  const classOf = (studentId: string): ClassRef | undefined => {
    const c = enrollments.find((e) => e.student_member_id === studentId)?.class;
    return c ? { id: c.id, grade: c.grade, label: classLabel(c) } : undefined;
  };
  const childRef = (p: { id: string; first_name: string; last_name: string }): ChildRef => {
    const klass = classOf(p.id);
    return {
      memberId: p.id,
      firstName: p.first_name,
      lastName: p.last_name,
      ...(klass && { class: klass }),
    };
  };

  const contexts: ViewContext[] = [];
  for (const m of me.memberships) {
    const base = { schoolId: m.school_id, schoolName: m.school_name, memberId: m.member_id };
    if (m.kind === 'guardian') {
      const mine = children
        .filter((c) => c.guardian_member_id === m.member_id)
        .map((c) => childRef(c.student))
        .sort((a, b) => (b.class?.grade ?? 0) - (a.class?.grade ?? 0));
      for (const child of mine) {
        contexts.push({ ...base, key: `guardian:${child.memberId}`, kind: 'guardian', child });
      }
    } else if (m.kind === 'student') {
      const person = self.find((p) => p.id === m.member_id);
      if (person) {
        contexts.push({
          ...base,
          key: `student:${m.member_id}`,
          kind: 'student',
          child: childRef(person),
        });
      }
    } else {
      if (m.roles.includes('teacher')) {
        const homeroom = homerooms.find((c) => c.homeroom_member_id === m.member_id);
        contexts.push({
          ...base,
          key: `teacher:${m.member_id}`,
          kind: 'teacher',
          ...(homeroom && {
            homeroomClass: { id: homeroom.id, grade: homeroom.grade, label: classLabel(homeroom) },
          }),
        });
      }
      const adminRoles = m.roles.filter((r) => ADMIN_ROLES.includes(r));
      if (adminRoles.length > 0) {
        contexts.push({ ...base, key: `admin:${m.member_id}`, kind: 'admin', roles: m.roles });
      }
      if (m.roles.includes('psychologist')) {
        contexts.push({ ...base, key: `psychologist:${m.member_id}`, kind: 'psychologist' });
      }
    }
  }
  return contexts;
}

export function useViewContexts(me: Me | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['contexts', me?.user_id, me?.aal],
    enabled: me !== undefined,
    queryFn: () => fetchViewContexts(db, me as Me),
    staleTime: 5 * 60_000,
  });
}
